import React, { useState, useEffect, useRef, useCallback, FC } from 'react'

import { gsap } from 'gsap'

import Button from '@components/Button'
import Icon from '@components/Icon'
import Select from '@components/Select'
import SelectLang from '@components/SelectLang'
import SelectProfile, { Profile } from '@components/SelectProfile'
import { Language } from '@controllers/parts/Header'
import { type MenuItemType } from '@controllers/parts/SidebarMenu'
import {
  trans,
  CLOSE_NAVIGATION,
  BACK,
  SEARCH,
  CHOOSE_LANGUAGE,
  SEE_ALL_RESULTS,
  NO_RESULTS_FOUND,
  SEARCH_IN_PROGRESS,
} from '@helpers/translator'
import { removeLanguagePrefixFromUrl } from '@helpers/urlUtils'
import useIsRTL from '@hooks/useIsRTL'
import { useTailwindBreakpoint } from '@hooks/useTailwindBreakpoint'

interface MenuColumnProps {
  items: MenuItemType[]
  depth: number
  onItemClick: (item: MenuItemType, depth: number) => void
  isItemActive: (item: MenuItemType, depth: number) => boolean
  includeViewAll?: boolean
  openColumns: MenuItemType[][]
}

const MenuColumn: FC<MenuColumnProps> = React.memo(
  ({
    items,
    depth,
    onItemClick,
    isItemActive,
    includeViewAll = false,
    openColumns,
  }) => {
    return (
      <ul className="flex-1 space-y-6 absolute" role="none">
        {items.map((item, index) => {
          const active = isItemActive(item, depth)

          if (item.showInMenu) {
            return (
              <li key={`item-${item.id || index}`} role="none">
                {item.subItems && item.subItems.length > 0 ? (
                  <button
                    onClick={() => onItemClick(item, depth)}
                    className={`w-full ltr:text-left rtl:text-right transition-all duration-200 ${
                      depth === 0 ? 'text-[21px]' : 'text-[18px]'
                    } ${active ? 'font-bold' : 'font-medium hover:font-bold'} text-deep-black`}
                    aria-haspopup="true"
                    aria-expanded={active}
                  >
                    {item.label}
                  </button>
                ) : item.url ? (
                  <a
                    href={item.url}
                    className={`w-full ltr:text-left rtl:text-right ${
                      depth === 0 ? 'text-lg' : 'text-[18px]'
                    } ${active ? 'font-bold' : 'font-medium hover:font-bold'} text-deep-black`}
                    role="menuitem"
                  >
                    {item.label}
                  </a>
                ) : (
                  <span className="w-full text-left text-deep-black hover:font-bold">
                    {item.label}
                  </span>
                )}
              </li>
            )
          }
        })}

        {includeViewAll && (
          <ViewAllLink depth={depth} openColumns={openColumns} items={items} />
        )}
      </ul>
    )
  },
)

interface ViewAllLinkProps {
  depth: number
  openColumns: MenuItemType[][]
  items: MenuItemType[]
}

const ViewAllLink: FC<ViewAllLinkProps> = React.memo(
  ({ depth, openColumns, items }) => {
    const parentItem = openColumns[depth - 1]?.find((parent) =>
      parent.subItems?.includes(items[0]),
    )
    if (parentItem?.viewAllLabel && parentItem.url) {
      return (
        <li role="none" className="mt-4" key={`view-all-${depth}`}>
          <a
            href={parentItem.url}
            className="flex items-center space-x-3 py-2 text-deep-black underline transition w-full text-left text-[18px] underline-offset-4 hover-bold-no-resize hover:font-bold duration-100"
            role="menuitem"
          >
            {parentItem.viewAllLabel}
          </a>
        </li>
      )
    }
    return null
  },
)

interface SearchResult {
  id: number
  title: string
  url: string
  content_type: string
}

interface InstantSearchResults {
  results: Record<string, SearchResult[]>
  content_types: string[]
  type_labels: Record<string, string>
  total: number
  query: string
  search_url: string
}

interface InstantSearchProps {
  onClose: () => void
  onSearchActiveChange?: (active: boolean) => void
}

const InstantSearch: FC<InstantSearchProps> = ({ onClose }) => {
  const [searchTerm, setSearchTerm] = useState('')
  const [isSearching, setIsSearching] = useState(false)
  const [searchResults, setSearchResults] =
    useState<InstantSearchResults | null>(null)
  const [hasShownFirstResults, setHasShownFirstResults] = useState(false)
  const instantSearchInputRef = useRef<HTMLInputElement>(null)
  const searchPanelRef = useRef<HTMLDivElement>(null)
  const searchResultsContainerRef = useRef<HTMLDivElement>(null)
  const isMobileView = useTailwindBreakpoint('md-')
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null)
  const currentSearchTermRef = useRef<string>('')

  // Fonction pour effectuer la recherche
  const performSearch = useCallback(async (searchTerm: string) => {
    if (searchTerm.length < 2) {
      setSearchResults(null)
      return
    }

    currentSearchTermRef.current = searchTerm

    setIsSearching(true)
    try {
      const response = await fetch(
        `/api/visitmonaco/instant-search?query=${encodeURIComponent(searchTerm)}&limit=5`,
      )
      if (response.ok) {
        const data = await response.json()
        if (
          currentSearchTermRef.current === searchTerm &&
          searchTerm.length >= 2
        ) {
          setSearchResults(data)
        }
      }
    } catch (error) {
      // Gestion silencieuse des erreurs de recherche
    } finally {
      if (currentSearchTermRef.current === searchTerm) {
        setIsSearching(false)
      }
    }
  }, [])

  // Fonction pour gérer la redirection vers la page de recherche
  const redirectToSearchPage = useCallback(
    (searchTerm: string) => {
      if (searchResults?.search_url) {
        // Utiliser l'URL de recherche multilingue fournie par le contrôleur
        window.location.href = searchResults.search_url
      } else {
        setIsSearching(true)
        fetch(
          `/api/visitmonaco/instant-search?query=${encodeURIComponent(searchTerm)}&limit=1`,
        )
          .then((response) => response.json())
          .then((data) => {
            if (data.search_url) {
              window.location.href = data.search_url
            }
          })
          .catch(() => {
            setIsSearching(false)
          })
      }
    },
    [searchResults, setIsSearching],
  )

  // Gestionnaire pour la touche Entrée - Redirection vers la page de recherche complète
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter' && searchTerm.length >= 2) {
        e.preventDefault()
        redirectToSearchPage(searchTerm)
      }
    },
    [searchTerm, redirectToSearchPage],
  )

  useEffect(() => {
    if (isSearching && searchResults) {
      return
    }
    if (
      searchResults &&
      !isSearching &&
      !hasShownFirstResults &&
      searchResultsContainerRef.current
    ) {
      const categories =
        searchResultsContainerRef.current.querySelectorAll('.search-category')
      gsap.fromTo(
        categories,
        { opacity: 0, y: -10 },
        {
          opacity: 1,
          y: 0,
          duration: 0.3,
          stagger: 0.1,
          ease: 'power2.out',
        },
      )

      const results = searchResultsContainerRef.current.querySelectorAll(
        '.search-result-item',
      )
      gsap.fromTo(
        results,
        { opacity: 0, x: -10 },
        {
          opacity: 1,
          x: 0,
          duration: 0.3,
          stagger: 0.05,
          delay: 0.1,
          ease: 'power2.out',
          onComplete: () => {
            setHasShownFirstResults(true)
          },
        },
      )
    }
  }, [searchResults, isSearching, hasShownFirstResults])

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value
      setSearchTerm(value)

      if (value.length === 0) {
        setHasShownFirstResults(false)
        setSearchResults(null)
        currentSearchTermRef.current = ''
        setIsSearching(false)
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current)
          debounceTimerRef.current = null
        }
        return
      }

      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }

      debounceTimerRef.current = setTimeout(() => {
        performSearch(value)
      }, 300)
    },
    [performSearch, setSearchResults, setHasShownFirstResults],
  )

  useEffect(() => {
    setTimeout(() => {
      instantSearchInputRef.current?.focus()
    }, 100)
  }, [])

  return (
    <div ref={searchPanelRef} className="w-full h-full flex flex-col">
      <div className="mb-8 ">
        <div className="border-b border-black">
          <button
            onClick={onClose}
            className={`absolute top-[92px] transition-all duration-300  z-10 cursor-pointer flex items-center h-5`}
            aria-label={trans(BACK)}
          >
            <Icon name="arrow_circle" size="22" />
            <span className="ltr:ml-2 rtl:mr-2 text-deep-black">
              {trans(BACK)}
            </span>
          </button>
          <div className="flex items-end ">
            <input
              ref={instantSearchInputRef}
              type="text"
              value={searchTerm}
              onChange={handleSearchChange}
              onKeyDown={handleKeyDown}
              className="border-none focus:ring-0 font-400 placeholder:text-gray-400 bg-transparent lg:text-[72px] text-[38px] pb-0 pl-0 w-full"
              placeholder={trans(SEARCH, {}, 'messages')}
              autoFocus
            />

            <button
              onClick={() => redirectToSearchPage(searchTerm)}
              className={`text-black mr-3 relative bottom-4 ${isSearching ? 'animate-pulse' : ''}`}
            >
              <Icon name="search" size="26" />
            </button>
          </div>
        </div>
      </div>

      <div
        ref={searchResultsContainerRef}
        className="flex-1 overflow-y-auto pb-2 scrollbar-y"
      >
        {isSearching && !searchResults && (
          <div className="flex flex-col items-center justify-center py-10">
            <div className="w-10 h-10 border-t-2 border-b-2 border-black rounded-full animate-spin mb-4"></div>
            <p className="text-gray-600">
              {trans(SEARCH_IN_PROGRESS, {}, 'messages')}
            </p>
          </div>
        )}

        {searchResults && (
          <>
            {searchResults.total === 0 && !isSearching ? (
              <div className="text-center text-gray-500 py-2 text-sm">
                {trans(NO_RESULTS_FOUND)}
              </div>
            ) : (
              <>
                <div
                  className={`md:flex md:flex-wrap md:gap-x-8 ${isSearching ? 'opacity-50' : ''}`}
                >
                  {searchResults.content_types.map((contentType) => (
                    <div
                      key={contentType}
                      className={`mb-6 md:w-[45%] search-category ${!hasShownFirstResults ? 'opacity-0' : 'opacity-100'}`}
                    >
                      <h3 className="font-semibold  text-grey-dark mb-2">
                        {searchResults.type_labels[contentType] || contentType}
                      </h3>
                      <ul className="space-y-2">
                        {searchResults.results[contentType].map((result) => (
                          <li
                            key={result.id}
                            className={`ml-4 search-result-item ${!hasShownFirstResults ? 'opacity-0' : 'opacity-100'}`}
                          >
                            <a
                              href={result.url}
                              className="text-deep-black hover:font-bold transition-all duration-200 text-sm line-clamp-1"
                            >
                              {result.title}
                            </a>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </div>
      {searchResults && (
        <>
          {searchResults.total > 0 && (
            <div
              className={`ltr:text-left rtl:text-right mt-6 mb-2 search-category self-end  ${!hasShownFirstResults ? 'opacity-0' : 'opacity-100'} ${isSearching ? 'opacity-50' : ''}`}
            >
              <Button
                href={searchResults.search_url}
                variant="secondary"
                icon={{
                  name: 'arrow',
                  size: '10',
                  color: 'white',
                  background: 'black',
                }}
              >
                {trans(SEE_ALL_RESULTS)}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  )
}

interface InstantSearchButtonProps {
  onClick: () => void
}

const InstantSearchButton: FC<InstantSearchButtonProps> = ({ onClick }) => (
  <button
    onClick={onClick}
    className="flex items-center border border-black rounded-full pr-4 pl-4 py-2 mb-8"
    aria-label={trans(SEARCH, {}, 'messages')}
  >
    <Icon name="search" size="18" className="text-black mr-2" />
    <span className="text-sm font-medium text-black">
      {trans(SEARCH, {}, 'messages')}
    </span>
  </button>
)

/**
 * Parcourt récursivement le menu en se basant sur la propriété isActive.
 * Renvoie la chaîne (du parent à l'enfant) qui correspond à la hiérarchie active.
 */
const getActiveChain = (items: MenuItemType[]): MenuItemType[] => {
  const currentUrl = window.location.href
  const currentDomain = window.location.origin
  const currentPathNormalized = removeLanguagePrefixFromUrl(
    window.location.pathname,
  )

  // PREMIÈRE PASSE : Chercher un élément avec isActive = true ET qui correspond au domaine actuel
  const findActiveWithDomainRecursive = (
    searchItems: MenuItemType[],
  ): MenuItemType[] => {
    for (const item of searchItems) {
      if (item.isActive && item.url && item.url.startsWith(currentDomain)) {
        const itemChain = [item]
        if (item.subItems && item.subItems.length > 0) {
          const subChain = findActiveWithDomainRecursive(item.subItems)
          return itemChain.concat(subChain)
        }
        return itemChain
      }
    }
    return []
  }

  const activeChainWithDomain = findActiveWithDomainRecursive(items)

  if (activeChainWithDomain.length > 0) return activeChainWithDomain

  // DEUXIÈME PASSE : Chercher un élément avec isActive = true (fallback si pas de correspondance de domaine)
  const findActiveRecursive = (searchItems: MenuItemType[]): MenuItemType[] => {
    for (const item of searchItems) {
      if (item.isActive) {
        const itemChain = [item]
        if (item.subItems && item.subItems.length > 0) {
          const subChain = findActiveRecursive(item.subItems)
          return itemChain.concat(subChain)
        }
        return itemChain
      }
    }
    return []
  }

  const activeChain = findActiveRecursive(items)
  if (activeChain.length > 0) return activeChain

  // TROISIÈME PASSE : Correspondance exacte d'URL complète
  const findExactUrlRecursive = (
    searchItems: MenuItemType[],
  ): MenuItemType[] => {
    for (const item of searchItems) {
      if (item.url === currentUrl) {
        const itemChain = [item]
        if (item.subItems && item.subItems.length > 0) {
          const subChain = findExactUrlRecursive(item.subItems)
          return itemChain.concat(subChain)
        }
        return itemChain
      }
      if (item.subItems && item.subItems.length > 0) {
        const subChain = findExactUrlRecursive(item.subItems)
        if (subChain.length > 0) {
          return [item, ...subChain]
        }
      }
    }
    return []
  }

  const exactUrlChain = findExactUrlRecursive(items)
  if (exactUrlChain.length > 0) return exactUrlChain

  // QUATRIÈME PASSE : Fallback sur l'URL normalisée seulement si aucune correspondance exacte
  const findNormalizedUrlRecursive = (
    searchItems: MenuItemType[],
  ): MenuItemType[] => {
    for (const item of searchItems) {
      if (
        item.url &&
        removeLanguagePrefixFromUrl(item.url) === currentPathNormalized
      ) {
        const itemChain = [item]
        if (item.subItems && item.subItems.length > 0) {
          const subChain = findNormalizedUrlRecursive(item.subItems)
          return itemChain.concat(subChain)
        }
        return itemChain
      }
      if (item.subItems && item.subItems.length > 0) {
        const subChain = findNormalizedUrlRecursive(item.subItems)
        if (subChain.length > 0) {
          return [item, ...subChain]
        }
      }
    }
    return []
  }

  return findNormalizedUrlRecursive(items)
}

/**
 * Recherche récursive du chemin d'un item dont l'URL correspond à targetUrl.
 */
const findChainByUrl = (
  items: MenuItemType[],
  targetUrl: string,
): MenuItemType[] | null => {
  // PREMIÈRE PASSE : Recherche exacte dans TOUT l'arbre avant le fallback
  const findExactMatch = (
    searchItems: MenuItemType[],
  ): MenuItemType[] | null => {
    for (const item of searchItems) {
      if (item.url === targetUrl) {
        return [item]
      }
      if (item.subItems && item.subItems.length > 0) {
        const subChain = findExactMatch(item.subItems)
        if (subChain) {
          return [item, ...subChain]
        }
      }
    }
    return null
  }

  // Essayer d'abord la correspondance exacte
  const exactMatch = findExactMatch(items)
  if (exactMatch) return exactMatch

  // DEUXIÈME PASSE : Fallback avec normalisation seulement si aucune correspondance exacte
  const normalizedTarget = removeLanguagePrefixFromUrl(targetUrl)

  const findNormalizedMatch = (
    searchItems: MenuItemType[],
  ): MenuItemType[] | null => {
    for (const item of searchItems) {
      const normalizedItemUrl = removeLanguagePrefixFromUrl(item.url || '')

      if (normalizedItemUrl === normalizedTarget) {
        return [item]
      }
      if (item.subItems && item.subItems.length > 0) {
        const subChain = findNormalizedMatch(item.subItems)
        if (subChain) {
          return [item, ...subChain]
        }
      }
    }
    return null
  }

  const normalizedMatch = findNormalizedMatch(items)
  if (normalizedMatch) return normalizedMatch

  return null
}

/**
 * Hook pour animer la dernière colonne avec GSAP.
 */
const useAnimateLastColumn = (
  openColumns: MenuItemType[][],
  columnRefs: React.MutableRefObject<(HTMLDivElement | null)[]>,
) => {
  const isRTL = useIsRTL()

  useEffect(() => {
    const lastIndex = openColumns.length - 1
    const lastColumnRef = columnRefs.current[lastIndex]
    if (lastColumnRef) {
      gsap.killTweensOf(lastColumnRef)
      gsap.fromTo(
        lastColumnRef,
        { x: isRTL ? 140 : -140, opacity: 0 },
        {
          x: 0,
          opacity: 1,
          duration: 0.75,
          ease: 'power2.out',
          stagger: 0.1,
        },
      )
    }
  }, [openColumns, columnRefs])
}

interface MultiColumnMenuProps {
  initPath?: string
  menuItems: MenuItemType[]
  onClose: () => void
  Footer?: React.ElementType
  socialLinks?: Record<string, any>
  languages: Language[]
  profiles: Profile[]
  activateSearch?: boolean
}

const MultiColumnMenu: FC<MultiColumnMenuProps> = ({
  initPath,
  menuItems,
  onClose,
  Footer,
  languages,
  profiles,
  activateSearch = false,
}) => {
  if (!menuItems.length) {
    return null
  }

  const isMobileView = useTailwindBreakpoint('md-')
  const isRTL = useIsRTL()

  const [isSearchActive, setIsSearchActive] = useState<boolean>(activateSearch)
  useEffect(() => {
    if (activateSearch) {
      setIsSearchActive(true)

      requestAnimationFrame(() => {
        if (searchOverlayRef.current) {
          gsap.fromTo(
            searchOverlayRef.current,
            {
              opacity: 0,
              x: 50,
            },
            {
              opacity: 1,
              x: 0,
              duration: 0.5,
              ease: 'power2.out',
            },
          )
        }
      })
    }
  }, [activateSearch])

  const [activeChain, setActiveChain] = useState<MenuItemType[]>([])
  const [openColumns, setOpenColumns] = useState<MenuItemType[][]>([menuItems])

  const columnRefs = useRef<(HTMLDivElement | null)[]>([])
  const navRef = useRef<HTMLElement | null>(null)
  const searchOverlayRef = useRef<HTMLDivElement>(null)
  const searchResultsRef = useRef<HTMLDivElement>(null)

  // Effect initial pour calculer l'activeChain
  useEffect(() => {
    if (menuItems.length > 0) {
      const initialChain = getActiveChain(menuItems) || [menuItems[0]]

      setActiveChain(initialChain)

      if (isMobileView) {
        const subColumns = initialChain
          .map((item) => item.subItems || [])
          .filter((subItems) => subItems.length > 0)

        setOpenColumns([menuItems, ...subColumns])
      }
    }
  }, [menuItems, isMobileView])

  useEffect(() => {
    const chainFromPath = initPath ? findChainByUrl(menuItems, initPath) : null
    const activeChainFromBackend = getActiveChain(menuItems)
    const currentUrl = window.location.href

    // Si initPath est différent de l'URL actuelle, c'est un vrai clic depuis sidebar
    const isRealSidebarClick = initPath && initPath !== currentUrl

    const chain =
      isRealSidebarClick && chainFromPath
        ? chainFromPath
        : activeChainFromBackend || (menuItems.length > 0 ? [menuItems[0]] : [])

    setActiveChain(chain)

    if (isMobileView) {
      const subColumns = chain
        .map((item) => item.subItems || [])
        .filter((subItems) => subItems.length > 0)

      const lastItem = chain.length > 0 ? chain[chain.length - 1] : null
      if (lastItem && (!lastItem.subItems || lastItem.subItems.length === 0)) {
        if (chain.length === 1) {
          setOpenColumns([menuItems])
        } else {
          const parentDepth = chain.length - 1
          const columnsToShow = [menuItems]

          for (let i = 0; i < parentDepth; i++) {
            const item = chain[i]
            if (item && item.subItems && item.subItems.length > 0) {
              columnsToShow.push(item.subItems)
            }
          }

          setOpenColumns(columnsToShow)
        }
      } else {
        setOpenColumns([menuItems, ...subColumns])
      }
    } else {
      setOpenColumns([menuItems])
      chain.forEach((item, index) => {
        const subItems = item.subItems || []
        if (subItems.length > 0) {
          setTimeout(
            () => {
              setOpenColumns((prevColumns) => [
                ...prevColumns.slice(0, index + 1),
                subItems,
              ])
            },
            (index + 1) * 300,
          )
        }
      })
    }
  }, [menuItems, isMobileView, initPath])

  useAnimateLastColumn(openColumns, columnRefs)

  const handleClose = useCallback(() => {
    setIsSearchActive(false)
    onClose()
  }, [onClose])

  const handleItemClick = useCallback(
    (item: MenuItemType, depth: number) => {
      if (
        !isMobileView &&
        activeChain[depth]?.url === item.url &&
        (!item.subItems || item.subItems.length === 0)
      )
        return

      const newChain = [...activeChain.slice(0, depth), item]
      setActiveChain(newChain)

      if (item.subItems && item.subItems.length > 0) {
        setOpenColumns((prevColumns) => [
          ...prevColumns.slice(0, depth + 1),
          item.subItems!,
        ])
      } else {
        setOpenColumns((prevColumns) => prevColumns.slice(0, depth + 1))
      }
    },
    [activeChain, isMobileView],
  )

  const handleBack = useCallback(
    (depth: number) => {
      if (depth === 1 && isMobileView) {
        handleClose()
      } else {
        const currentColumn = columnRefs.current[depth]
        if (currentColumn) {
          gsap.to(currentColumn, {
            x: 70,
            opacity: 0,
            duration: 0.5,
            ease: 'power2.in',
            onComplete: () => {
              setOpenColumns(openColumns.slice(0, depth))
              setActiveChain(activeChain.slice(0, depth - 1))
            },
          })
        }
      }
    },
    [openColumns, activeChain, isMobileView, handleClose],
  )

  const handleActivateSearch = useCallback(() => {
    setIsSearchActive(true)

    requestAnimationFrame(() => {
      if (searchOverlayRef.current) {
        gsap.fromTo(
          searchOverlayRef.current,
          {
            opacity: 0,
            x: 50,
          },
          {
            opacity: 1,
            x: 0,
            duration: 0.5,
            ease: 'power2.out',
          },
        )
      }
    })
  }, [])

  // Vérifier si la recherche instantanée doit être affichée
  const shouldShowInstantSearch =
    activeChain[0]?.subItems?.some(
      (subItem) => subItem.isInstantSearch === true,
    ) || false

  // Fermer automatiquement la recherche si elle n'est plus disponible
  useEffect(() => {
    if (isSearchActive && !shouldShowInstantSearch) {
      setIsSearchActive(false)
    }
  }, [isSearchActive, shouldShowInstantSearch])

  const searchButton = shouldShowInstantSearch ? (
    <button
      onClick={handleActivateSearch}
      className="flex items-center border border-black rounded-full -ml-2 pr-3 pl-4 py-2 mb-8"
      aria-label={trans(SEARCH, {}, 'messages')}
    >
      <span className="text-black">Recherche</span>
      <Icon name="search" size="20" className="text-black ltr:ml-2 rtl:mr-2" />
    </button>
  ) : null

  const getColumnStyle = (depth: number): React.CSSProperties =>
    isMobileView
      ? { width: '100%', left: '0' }
      : {
          width: `calc(25vw * ${depth + 1})`,
          left: isRTL ? '0' : `calc(${depth} * 25vw)`,
          right: isRTL ? `calc(${depth} * 25vw)` : '0',
        }

  const getBgColorForColumn = (
    depth: number,
  ): { className: string; style?: React.CSSProperties } => {
    const bgColorRaw = activeChain[0]?.bgColor

    if (Array.isArray(bgColorRaw)) {
      const index = depth < 2 ? 0 : depth - 1
      const value = bgColorRaw[index] || 'bg-white'
      if (typeof value === 'string' && value.startsWith('bg-')) {
        return { className: value }
      } else {
        return { className: '', style: { backgroundColor: value } }
      }
    } else if (typeof bgColorRaw === 'string' && bgColorRaw) {
      if (bgColorRaw.startsWith('bg-')) {
        return { className: bgColorRaw }
      } else {
        return { className: '', style: { backgroundColor: bgColorRaw } }
      }
    }
    return { className: 'bg-white' }
  }

  const isItemBold = (item: MenuItemType): boolean => {
    const index = activeChain.findIndex(
      (activeItem) => activeItem.url === item.url,
    )
    return index !== -1
  }

  return (
    <div
      className={`flex h-screen w-full relative transition-colors duration-500 z-[1000] ${getBgColorForColumn(0).className}`}
    >
      <nav
        ref={navRef}
        aria-label={trans('NAVIGATION')}
        className="flex h-full text-deep-black relative w-full"
      >
        <button
          onClick={handleClose}
          className="absolute top-6 ltr:left-8 rtl:right-8 z-[30] cursor-pointer flex items-center h-5"
          aria-label={trans(CLOSE_NAVIGATION)}
        >
          <div className="w-5 h-5 relative">
            <div className="transition-all duration-[800ms] absolute bg-deep-black h-5 w-[3px] rotate-45" />
            <div className="transition-all duration-[800ms] absolute bg-deep-black h-5 w-[3px] -rotate-45" />
          </div>
        </button>

        <div
          className={`absolute top-[80px] transition-all duration-300 ltr:left-10 rtl:right-10  ${
            isMobileView && openColumns.length <= 2 && !isSearchActive
              ? 'opacity-100 translate-x-0 z-[1000]'
              : 'opacity-0 -translate-x-10 z-1'
          }`}
        >
          {searchButton}
        </div>

        <button
          onClick={() => handleBack(openColumns.length - 1)}
          className={`absolute top-[92px] transition-all duration-300 ltr:left-8 rtl:right-8 z-10 cursor-pointer flex items-center h-5 ${
            isMobileView && openColumns.length > 2
              ? 'opacity-100 translate-x-0'
              : 'opacity-0 -translate-x-10'
          }`}
          aria-label={trans(BACK)}
        >
          <Icon name="arrow_circle" size="22" />
          <span className="ml-2 text-deep-black">{trans(BACK)}</span>
        </button>

        {openColumns.map((columnItems, depth) => {
          if (!columnItems || columnItems.length === 0) return null
          const columnBg = getBgColorForColumn(depth)
          return (
            <div
              key={`column-${depth}`}
              ref={(el) => (columnRefs.current[depth] = el)}
              className={`${
                (depth < openColumns.length - 1 ? 'hidden sm:block' : 'block') +
                (isSearchActive && depth > 0 ? ' hidden' : '')
              } absolute top-0 h-full py-4 pt-36 ltr:pl-8 rtl:pr-8 ${
                depth === 1 && !isMobileView && !isSearchActive
                  ? 'ltr:sm:border-l rtl:sm:border-r sm:border-black/10'
                  : ''
              } ${columnBg.className}`}
              style={{
                ...getColumnStyle(depth),
                ...columnBg.style,
                zIndex: isSearchActive ? 20 : 10,
                display: isSearchActive && depth > 0 ? 'none' : undefined,
              }}
              aria-expanded={depth === openColumns.length - 1}
            >
              {depth === 1 &&
                !isMobileView &&
                !isSearchActive &&
                shouldShowInstantSearch && (
                  <InstantSearchButton onClick={handleActivateSearch} />
                )}

              <MenuColumn
                items={columnItems}
                depth={depth}
                onItemClick={handleItemClick}
                isItemActive={(item) =>
                  activeChain.some((active) => active.url === item.url)
                }
                includeViewAll
                openColumns={openColumns}
              />
            </div>
          )
        })}

        {!isSearchActive && (
          <button
            onClick={() => handleBack(openColumns.length - 1)}
            className={`absolute top-[92px] transition-all duration-300 ltr:left-8 rtl:right-24 z-10 cursor-pointer flex items-center h-5 ${
              isMobileView && openColumns.length > 2
                ? 'opacity-100 translate-x-0'
                : 'opacity-0 -translate-x-10'
            }`}
            aria-label={trans(BACK)}
          >
            <Icon name="arrow_circle" size="22" />
            <span className="ml-2 text-deep-black">{trans(BACK)}</span>
          </button>
        )}

        {isSearchActive && (
          <div
            ref={searchOverlayRef}
            className="absolute top-0 ltr:left-0 rtl:right-0 w-full h-full z-[25] ltr:border-l rtl:border-r border-black/10"
            style={{
              [isRTL ? 'marginRight' : 'marginLeft']: isMobileView ? 0 : '25vw',
              width: isMobileView ? '100%' : 'calc(75vw - 1px)',
              opacity: 0,
            }}
          >
            <div
              ref={searchResultsRef}
              className="h-full p-8 pt-36 pb-20 overflow-hidden max-w-5xl mx-auto"
            >
              <InstantSearch onClose={() => setIsSearchActive(false)} />
            </div>
          </div>
        )}
      </nav>

      {openColumns.length === 2 && !isSearchActive && (
        <Select
          className="block md:hidden z-[1000] absolute bottom-20 ltr:left-7 rtl:right-7"
          placeholder={trans(CHOOSE_LANGUAGE)}
          options={languages.map((language) => ({
            value: language.url,
            label: language.label,
            selected: language.selected,
          }))}
          onChange={(value) => {
            const selectedOption = Array.isArray(value) ? value[0] : value
            if (selectedOption) {
              window.location.href = selectedOption.value.toString() || ''
            }
          }}
          openUpwards={true}
        />
      )}

      <SelectProfile
        className={`md:hidden absolute ltr:right-0 rtl:left-0 z-[1000] top-0 min-w-[199px] ${isSearchActive ? 'hidden' : ''}`}
        profiles={profiles}
      />
      <SelectLang
        className={`hidden md:block absolute ltr:right-0 rtl:left-0 z-[1000] top-0 min-w-[65px] ${isSearchActive ? 'hidden' : ''}`}
        languages={languages}
      />
      {Footer && !isSearchActive && <Footer />}
    </div>
  )
}

export default React.memo(MultiColumnMenu)
